import React, {Fragment, PureComponent} from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import {
  DataBlocks,
  I18n,
} from 'components';

import {
  CONFIRMATION_POPUP_KEY,
  EDITING_POPUP_KEY,
} from './constants';

import NotifiableActions from './components/NotifiableActions';
import NotifiableActionsForTvConsoles from './components/NotifiableActionsForTvConsoles';
import PeriodicRequest from './components/PeriodicRequest';
import HomeNotificationPoints from './containers/HomeNotificationPoints';

import {
  saveActions,
  toggleNotificationPoint,
  togglePointEditingState,
  saveNotificationPoint,
  confirmNotificationPointCode,
  resetNotificationPointCodeValidation,
  sendNotificationPointCode,
  toggleConfirmationCodePopup,
  saveRequest,
  fetchInitialData,
  fetchActions,
  fetchRequest,
  fetchPoints,
  fetchTvActions,
  saveTvActions,
} from './actions';
import NotificationPointEditPopup from '../../components/NotificationPointEditPopup';
import { NOTIFICATION_POINTS_TYPES } from '../../constants';
import { formatPhone } from '../../../../utils/formatString';
import ConfirmationCodePopup from '../../components/ConfirmationCodePopup';
import NotificationPoints from '../../components/NotificationPoints';
import { getNotificationPointsBlockEnabled } from '../../../../utils/common';

class Notifications extends PureComponent {
  componentDidMount() {
    this.props.actions.fetchInitialData();
  }

  toGenerateDataForPeriodicRequest = () => {
    const periodicReqData = {};
    const { periodicRequest } = this.props;

    Object.keys(periodicRequest).forEach((fieldName) => {
      if (fieldName === 'numberStatus') {
        periodicReqData[fieldName] = periodicRequest[fieldName].filter((number) => number.available);
      } else {
        periodicReqData[fieldName] = periodicRequest[fieldName];
      }
    });

    return periodicReqData;
  };

  render() {
    const {
      isPostpaid,
      isConvergent,
      actions,
      notifiableActions,
      notificationPoints,
      periodicRequest,
      isActionsSaving,
      isRequestSaving,
      actionsFetchingError,
      requestFetchingError,
      isConfirmationOpened,
      isPointEditing,
      pointsFetchingError,
    } = this.props;

    const notificationPointsBlockEnabled = getNotificationPointsBlockEnabled();

    const showPeriodicRequestBlock = !isPostpaid
      && periodicRequest
      && !!periodicRequest.numberStatus.length
      && !periodicRequest.numberStatus.every((number) => !number.available);

    const emailPoint = notificationPoints
      ? notificationPoints.find((p) => p.type === 'EMAIL') : {};

    const getPointValue = (value, type) => (
      type === NOTIFICATION_POINTS_TYPES.SMS
        ? formatPhone(value)
        : value
    );

    return (
      <DataBlocks.Container>
        {notificationPointsBlockEnabled && (
          <NotificationPoints
            title={<I18n path="settings.notifications.title" />}
            notificationPoints={notificationPoints}
            isConfirmationOpened={isConfirmationOpened}
            isPointEditing={isPointEditing}
            toggleNotificationPoint={actions.toggleNotificationPoint}
            togglePointEditingState={actions.togglePointEditingState}
            saveNotificationPoint={actions.saveNotificationPoint}
            confirmNotificationPointCode={actions.confirmNotificationPointCode}
            resetNotificationPointCodeValidation={actions.resetNotificationPointCodeValidation}
            sendNotificationPointCode={actions.sendNotificationPointCode}
            toggleConfirmationCodePopup={actions.toggleConfirmationCodePopup}
            error={pointsFetchingError}
            refreshAction={actions.fetchPoints}
          />
        )}
        {!notificationPointsBlockEnabled && notificationPoints && notificationPoints.map((point) => {
          const pointValue = getPointValue(point.value, point.type);
          return (
            <Fragment
              key={pointValue}
            >
              <NotificationPointEditPopup
                value={point.value || ''}
                type={point.type}
                isRecoveryPassword={false}
                isSaving={point.isSaving}
                savePointValue={(newValue) => actions.saveNotificationPoint({
                  type: point.type,
                  value: newValue || '',
                })}
                cancelPointValue={() => actions.togglePointEditingState({
                  type: point.type,
                  isEditing: false,
                })}
                isEditing={isPointEditing && point.isEditing}
              />
              <ConfirmationCodePopup
                isOpened={isConfirmationOpened}
                pointValue={pointValue}
                type={point.type}
                isCodeInvalid={point.isCodeInvalid}
                isChecking={point.isChecking}
                isCodeSending={point.isCodeSending}
                confirmCode={(confirmationCode) => actions.confirmNotificationPointCode({
                  type: point.type,
                  confirmationCode,
                })}
                resetPointCodeValidation={() => actions.resetNotificationPointCodeValidation(point.type)}
                resendCode={() => actions.sendNotificationPointCode(point.type)}
                cancelConfirmation={() => actions.toggleConfirmationCodePopup({
                  type: point.type,
                  isConfirmationOpened: false,
                })}
              />
            </Fragment>
          );
        })}

        {isConvergent && (
          <HomeNotificationPoints
            title={<I18n path="settings.notifications.homePointsTitle" />}
            isConfirmationOpened={isConfirmationOpened}
            isPointEditing={isPointEditing}
          />
        )}

        <NotifiableActions
          actions={notifiableActions}
          saveActions={actions.saveActions}
          isActionsSaving={isActionsSaving}
          error={actionsFetchingError}
          refreshAction={actions.fetchActions}
        />

        {showPeriodicRequestBlock && (
          <PeriodicRequest
            {...this.toGenerateDataForPeriodicRequest()}
            email={emailPoint.value}
            confirmStatus={emailPoint.isConfirmed}
            error={requestFetchingError}
            onSave={actions.saveRequest}
            onEmailEdit={() => actions.togglePointEditingState({ ...emailPoint, isEditing: true })}
            refreshAction={actions.fetchRequest}
            isSaving={isRequestSaving}
          />
        )}

        {isConvergent && (
          <NotifiableActionsForTvConsoles
            tvConsoles={this.props.tvConsoles}
            isTvConsolesFetching={this.props.isTvConsolesFetching}
            tvConsolesFetchingError={this.props.tvConsolesFetchingError}
            fetchTvActions={this.props.actions.fetchTvActions}
            saveTvActions={this.props.actions.saveTvActions}
          />
        )}
      </DataBlocks.Container>
    );
  }
}

Notifications.propTypes = {
  isPostpaid: PropTypes.bool,
  isConvergent: PropTypes.bool,
  actionsFetchingError: PropTypes.string,
  pointsFetchingError: PropTypes.string,
  requestFetchingError: PropTypes.string,
  actions: PropTypes.object,
  notifiableActions: PropTypes.array,
  notificationPoints: PropTypes.array,
  periodicRequest: PropTypes.object,
  isActionsSaving: PropTypes.bool,
  isConfirmationOpened: PropTypes.bool,
  isPointEditing: PropTypes.bool,
  isRequestSaving: PropTypes.bool,
  tvConsoles: PropTypes.array,
  isTvConsolesFetching: PropTypes.bool,
  tvConsolesFetchingError: PropTypes.string,
};

const mapStateToProps = (state) => ({
  isPostpaid: state.application.isPostpaid,
  isConvergent: state.application.isConvergent,
  notifiableActions: state.settings.notifications.actions,
  notificationPoints: state.settings.notifications.points,
  periodicRequest: state.settings.notifications.request,
  isActionsSaving: state.settings.notifications.isActionsSaving,
  isRequestSaving: state.settings.notifications.isRequestSaving,
  actionsFetchingError: state.settings.notifications.actionsFetchingError,
  pointsFetchingError: state.settings.notifications.pointsFetchingError,
  requestFetchingError: state.settings.notifications.requestFetchingError,
  isConfirmationOpened: state.application.popup === CONFIRMATION_POPUP_KEY,
  isPointEditing: state.application.popup === EDITING_POPUP_KEY,
  tvConsoles: state.settings.notifications.tvConsoles.data,
  isTvConsolesFetching: state.settings.notifications.tvConsoles.isFetching,
  tvConsolesFetchingError: state.settings.notifications.tvConsoles.error,
});

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators({
    saveActions,
    toggleNotificationPoint,
    togglePointEditingState,
    saveNotificationPoint,
    confirmNotificationPointCode,
    resetNotificationPointCodeValidation,
    sendNotificationPointCode,
    toggleConfirmationCodePopup,
    saveRequest,
    fetchInitialData,
    fetchActions,
    fetchPoints,
    fetchRequest,
    fetchTvActions,
    saveTvActions,
  }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(Notifications);
